import * as React from "react";
import { homeLink, linksOf, request } from "../../../services/cpln";
import { Kind, List } from "../../../schema/types/base";
import { Query, QuerySort } from "../../../schema/types/query/query";

export interface UseTableItemQueryDataOptions {
  fetchOnInit?: boolean;
  query?: Query;
  clientFilter?: (item: any) => boolean;
}

const defaultOptions: UseTableItemQueryDataOptions = {
  fetchOnInit: true,
  query: undefined,
  clientFilter: undefined,
};

export function useTableItemQueryData<T>(kind: Kind, _opts: UseTableItemQueryDataOptions = {}) {
  const opts = React.useMemo(() => ({ ...defaultOptions, ..._opts }), [kind, JSON.stringify(_opts)]);

  const [isLoading, setIsLoading] = React.useState(false);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [items, setItems] = React.useState<T[]>([]);
  const [nextLink, setNextLink] = React.useState<string | null>(null);

  // Sort state (from table header clicks)
  const defaultSort: QuerySort = { by: "name", order: "asc" };
  const [sort, setSort] = React.useState<QuerySort>(defaultSort);
  // Search state (from global search input)
  const [search, setSearch] = React.useState("");
  // CPLN Query builder state
  const [cplnQuery, setCplnQuery] = React.useState<Query | null>(null);

  const hasMore = !!nextLink;

  // Compose the effective query from all sources
  const effectiveQuery = React.useMemo<Query>(() => {
    // When CPLN Query builder is active, it takes full control
    if (cplnQuery) {
      return cplnQuery;
    }

    const baseTerms = opts.query?.spec?.terms || [];
    const baseMatch = opts.query?.spec?.match || "all";

    // When base query uses "none" (exclusion) and search is active,
    // search can't be added to the same terms — it would be negated.
    // Send search-only query to server; exclusion applied client-side via clientFilter.
    if (baseMatch === "none" && search) {
      return {
        kind: kind as any,
        spec: {
          match: "all" as const,
          terms: [{ property: "name", op: "~" as const, value: search }],
          sort: sort,
        },
      };
    }

    // Normal path: merge base terms + search
    const terms = [...baseTerms];
    if (search) {
      terms.push({ property: "name", op: "~", value: search });
    }

    return {
      kind: kind as any,
      spec: {
        match: baseMatch,
        terms: terms,
        sort: sort,
      },
    };
  }, [cplnQuery, opts.query, sort, search, kind]);

  async function fetchItems(automated: boolean = false) {
    try {
      if (!automated) {
        setIsLoading(true);
        setItems([]);
        setNextLink(null);
      }

      const url = homeLink(kind) + "/-query";

      const { data } = await request<List<T>>({ method: "post", url, body: effectiveQuery });
      setItems(data.items);
      setNextLink(linksOf(data).next || null);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      console.error(`Failed to fetch items of ${kind}`, errorMessage);
    }
  }

  async function fetchNextPage() {
    if (!nextLink || isLoadingMore) return;
    try {
      setIsLoadingMore(true);
      const { data } = await request<List<T>>({ url: nextLink });
      setItems((prev) => [...prev, ...data.items]);
      setNextLink(linksOf(data).next || null);
      setIsLoadingMore(false);
    } catch (e) {
      setIsLoadingMore(false);
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      console.error(`Failed to fetch next page of ${kind}`, errorMessage);
    }
  }

  // Fetch on init
  const [initialFetchDone, setInitialFetchDone] = React.useState(false);
  React.useEffect(() => {
    if (opts.fetchOnInit) {
      fetchItems();
    }
    setInitialFetchDone(true);
  }, []);

  // Refetch when effective query changes (after initial fetch)
  React.useEffect(() => {
    if (!initialFetchDone) return;
    fetchItems();
  }, [effectiveQuery]);

  async function refetch(automated: boolean = false) {
    await fetchItems(automated);
  }

  // Apply client-side filter when provided (used when server query can't express the full filter)
  const filteredItems = React.useMemo(() => {
    if (!_opts.clientFilter) return items;
    return items.filter(_opts.clientFilter);
  }, [items, _opts.clientFilter]);

  return {
    items: filteredItems,
    isLoading,
    isLoadingMore,
    hasMore,
    fetchItems,
    fetchNextPage,
    refetch,
    sort,
    setSort,
    search,
    setSearch,
    cplnQuery,
    setCplnQuery,
    visibleItems: filteredItems,
    // Keep these for backwards compat with CPLN Query builder
    query: cplnQuery,
    setQuery: setCplnQuery,
    isLoadingQueried: isLoading,
    fetchQueriedItems: fetchItems,
  };
}
